import api from "../../api";
import logger from "../../services/Logger";

// In-memory cache: avoids cold read on send
const _cache = new Map();    // request scope -> { data, expiresAt }
const _pending = new Map();  // request scope -> Promise  (deduplicates concurrent requests)
const CACHE_TTL_MS = 60 * 1000; // 1 minute

const getRequestKey = (uid, ownerId, spaceId) =>
    `${uid}:${ownerId || ""}:${spaceId || ""}`;

/**
 * Pre-warm the cache for a chat when it loads.
 * Stores the in-flight promise so any concurrent call (e.g. on send)
 * reuses it instead of starting a second request on the same cold connection.
 */
export const prewarmChatData = (uid, ownerId = null, spaceId = null) => {
  if (!uid) return;
    getCacheAndFileData(uid, ownerId, spaceId).catch(() => {}); // stores promise in _pending automatically
};

export const invalidateChatDataCache = (uid) => {
    if (!uid) return;
    const prefix = `${uid}:`;
    for (const key of _cache.keys()) {
        if (key.startsWith(prefix)) _cache.delete(key);
    }
};

export const getCacheAndFileData = async (uid, ownerId = null, spaceId = null) => {
    // Validate input
    if (!uid || typeof uid !== 'string') {
        logger.warn('getCacheAndFileData', 'Invalid chat ID provided', { uid });
        return null;
    }

    // Return from cache if still fresh
    const requestKey = getRequestKey(uid, ownerId, spaceId);
    const cached = _cache.get(requestKey);
    if (cached && Date.now() < cached.expiresAt) {
        return cached.data;
    }

    // Reuse in-flight request if one is already pending (e.g. prewarm still in progress)
    if (_pending.has(requestKey)) {
        return _pending.get(requestKey);
    }

    const promise = api({
        method: "GET",
        url: `/spark/${uid}/meta`,
        params: {
            ...(ownerId ? { owner_id: ownerId } : {}),
            ...(spaceId ? { space_id: spaceId } : {}),
        },
    })
        .then((response) => {
            const data = response?.data?.data ?? response?.data ?? null;
            _cache.set(requestKey, { data, expiresAt: Date.now() + CACHE_TTL_MS });
            _pending.delete(requestKey);
            return data;
        })
        .catch((error) => {
            _pending.delete(requestKey);
            logger.error('getCacheAndFileData', 'Error fetching spark meta', { error: error.message, uid });
            return null;
        });

    _pending.set(requestKey, promise);
    return promise;
};
